import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Bot,
  X,
} from "lucide-react";

import emailjs from "@emailjs/browser";

import {
  availableServices,
  findServiceFromMessage,
  findSubServiceFromMessage,
  generalServiceKeywords,
} from "./servicesData";

// ============================================================
// EMAILJS
// ============================================================

const sendUserInterestEmail = async ({
  name,
  email,
  services,
  service,
  subService,
}) => {
  try {
    // ----------------------------------------------------------
    // IMPORTANT
    // EmailJS template uses:
    //
    // {{user_name}}
    // {{user_email}}
    // {{services}}
    // {{admin_email}}
    //
    // ----------------------------------------------------------

    const templateParams = {
      user_name:
        name || "Not provided",

      user_email:
        email || "Not provided",

      // MAIN FIELD USED BY YOUR EMAILJS TEMPLATE
      services:
        services || "Not selected",

      // --------------------------------------------------------
      // These are also kept for compatibility with older
      // EmailJS templates.
      // --------------------------------------------------------

      interested_service:
        service || "Not selected",

      interested_sub_service:
        subService || "Not selected",

      submitted_at:
        new Date().toLocaleString(),

      admin_email:
        process.env
          .REACT_APP_EMAILJS_AI_CHATBOT_ADMIN_EMAIL,
    };

    console.log(
      "EmailJS template params:",
      templateParams,
    );

    const response =
      await emailjs.send(
        process.env
          .REACT_APP_EMAILJS_AI_CHATBOT_SERVICE_ID,

        process.env
          .REACT_APP_EMAILJS_AI_CHATBOT_TEMPLATE_ID,

        templateParams,

        process.env
          .REACT_APP_EMAILJS_AI_CHATBOT_PUBLIC_KEY,
      );

    console.log(
      "Chatbot lead sent successfully:",
      response,
    );

    return true;
  } catch (error) {
    console.error(
      "EmailJS chatbot error:",
      error,
    );

    return false;
  }
};

// ============================================================
// EMAIL VALIDATION
// ============================================================

const isValidEmail = (
  email = "",
) => {
  const value =
    email.trim().toLowerCase();

  const emailPattern =
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  return emailPattern.test(
    value,
  );
};

// ============================================================
// NORMALIZE TEXT
// ============================================================

const normalizeText = (
  value = "",
) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s&+.-]/g, " ")
    .replace(/\s+/g, " ");
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ServicesAssistant({
  isOpen,
  onClose,
}) {
  // ==========================================================
  // STATE
  // ==========================================================

  const [
    messages,
    setMessages,
  ] = useState([]);

  const [
    inputValue,
    setInputValue,
  ] = useState("");

  const [
    isTyping,
    setIsTyping,
  ] = useState(false);

  // ----------------------------------------------------------
  // EMAIL
  // ----------------------------------------------------------

  const [
    emailVerified,
    setEmailVerified,
  ] = useState(false);

  const [
    userEmail,
    setUserEmail,
  ] = useState("");

  // ----------------------------------------------------------
  // NAME
  // ----------------------------------------------------------

  const [
    nameCollected,
    setNameCollected,
  ] = useState(false);

  const [
    userName,
    setUserName,
  ] = useState("");

  // ----------------------------------------------------------
  // CURRENT SERVICE
  // ----------------------------------------------------------

  const [
    selectedService,
    setSelectedService,
  ] = useState(null);

  const [
    selectedSubService,
    setSelectedSubService,
  ] = useState(null);

  // ----------------------------------------------------------
  // ALL SERVICES OF INTEREST
  //
  // IMPORTANT:
  // This stores EVERY service discussed/selected.
  // ----------------------------------------------------------

  const [
    interestedServices,
    setInterestedServices,
  ] = useState([]);

  // ----------------------------------------------------------
  // EMAIL SENT
  // ----------------------------------------------------------

  const [
    emailSent,
    setEmailSent,
  ] = useState(false);

  // ==========================================================
  // REFS
  // ==========================================================

  const messagesEndRef =
    useRef(null);

  const greetedRef =
    useRef(false);

  const automaticQuestionShownRef =
    useRef(false);

  const timersRef =
    useRef([]);

  // ----------------------------------------------------------
  // IMPORTANT:
  // Keep a synchronous copy of interested services.
  //
  // React state updates are asynchronous, so this ref makes
  // sure EmailJS always receives the latest service list.
  // ----------------------------------------------------------

  const interestedServicesRef =
    useRef([]);

  // ==========================================================
  // AUTO SCROLL
  // ==========================================================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView(
      {
        behavior: "smooth",
      },
    );
  }, [
    messages,
    isTyping,
  ]);

  // ==========================================================
  // CLEAR ALL TIMERS
  // ==========================================================

  const clearAllTimers = () => {
    timersRef.current.forEach(
      (timer) => {
        clearTimeout(timer);
      },
    );

    timersRef.current = [];
  };

  // ==========================================================
  // RESET CHAT
  // ==========================================================

  const resetChat = () => {
    clearAllTimers();

    setMessages([]);

    setInputValue("");

    setIsTyping(false);

    setEmailVerified(false);

    setNameCollected(false);

    setUserEmail("");

    setUserName("");

    setSelectedService(null);

    setSelectedSubService(null);

    setInterestedServices([]);

    setEmailSent(false);

    // --------------------------------------------------------
    // IMPORTANT:
    // Also clear the ref.
    // --------------------------------------------------------

    interestedServicesRef.current = [];

    greetedRef.current = false;

    automaticQuestionShownRef.current =
      false;
  };

  // ==========================================================
  // MESSAGE HELPERS
  // ==========================================================

  function addMessage(message) {
    setMessages(
      (previous) => [
        ...previous,
        {
          id:
            Date.now() +
            Math.random(),

          ...message,
        },
      ],
    );
  }

  function botSay(
    text,
    extra = {},
  ) {
    addMessage({
      role: "bot",
      text,
      ...extra,
    });
  }

  function userSay(text) {
    addMessage({
      role: "user",
      text,
    });
  }

  // ==========================================================
  // TYPING EFFECT
  // ==========================================================

  function withTyping(
    callback,
    delay = 650,
  ) {
    setIsTyping(true);

    const timer =
      setTimeout(() => {
        setIsTyping(false);

        callback();
      }, delay);

    timersRef.current.push(
      timer,
    );
  }

  // ==========================================================
  // ADD SERVICE INTEREST
  //
  // This is the most important new function.
  //
  // It keeps ALL services discussed during the chat.
  //
  // Example:
  //
  // Software Development
  // WhatsApp API Services
  //
  // It also stores sub-services:
  //
  // Software Development
  //   - Web Development
  //   - Mobile App Development
  //
  // WhatsApp API Services
  //   - Business SMS
  //
  // ==========================================================

  function addServiceInterest(
    service,
    subService = null,
  ) {
    if (!service) {
      return;
    }

    const serviceName =
      service.name ||
      "Unknown Service";

    const subServiceName =
      subService?.name ||
      null;

    // --------------------------------------------------------
    // Check if this service already exists.
    // --------------------------------------------------------

    const existingIndex =
      interestedServicesRef.current.findIndex(
        (item) =>
          item.service ===
          serviceName,
      );

    // ========================================================
    // SERVICE ALREADY EXISTS
    // ========================================================

    if (
      existingIndex !== -1
    ) {
      const updated =
        [
          ...interestedServicesRef.current,
        ];

      // ------------------------------------------------------
      // Add sub-service if provided.
      // ------------------------------------------------------

      if (
        subServiceName
      ) {
        const existingSubServices =
          updated[
            existingIndex
          ].subServices || [];

        const alreadyExists =
          existingSubServices.includes(
            subServiceName,
          );

        if (
          !alreadyExists
        ) {
          updated[
            existingIndex
          ] = {
            ...updated[
              existingIndex
            ],

            subServices: [
              ...existingSubServices,
              subServiceName,
            ],
          };
        }
      }

      interestedServicesRef.current =
        updated;

      setInterestedServices(
        updated,
      );

      return;
    }

    // ========================================================
    // NEW SERVICE
    // ========================================================

    const newInterest = {
      service:
        serviceName,

      subServices:
        subServiceName
          ? [subServiceName]
          : [],
    };

    const updated = [
      ...interestedServicesRef.current,
      newInterest,
    ];

    interestedServicesRef.current =
      updated;

    setInterestedServices(
      updated,
    );
  }

  // ==========================================================
  // COLLECT SERVICES FROM USER MESSAGE
  //
  // This allows the chatbot to detect multiple services
  // mentioned in a single typed message.
  //
  // Example:
  //
  // "I need web development and WhatsApp API services"
  //
  // Both can be added to the interest list.
  // ==========================================================

  function collectServicesFromMessage(
    message,
  ) {
    if (!message) {
      return;
    }

    const normalizedMessage =
      normalizeText(
        message,
      );

    if (
      !normalizedMessage
    ) {
      return;
    }

    // --------------------------------------------------------
    // Scan all available services.
    // --------------------------------------------------------

    availableServices.forEach(
      (service) => {
        if (!service) {
          return;
        }

        const possibleNames = [
          service.name,
          service.title,
          service.id,
          ...(Array.isArray(
            service.keywords,
          )
            ? service.keywords
            : []),
        ].filter(Boolean);

        const serviceMatched =
          possibleNames.some(
            (name) => {
              const normalizedName =
                normalizeText(
                  name,
                );

              if (
                !normalizedName
              ) {
                return false;
              }

              return normalizedMessage.includes(
                normalizedName,
              );
            },
          );

        if (
          !serviceMatched
        ) {
          return;
        }

        // ------------------------------------------------------
        // Save main service.
        // ------------------------------------------------------

        addServiceInterest(
          service,
        );

        // ------------------------------------------------------
        // Check sub-services.
        // ------------------------------------------------------

        if (
          !Array.isArray(
            service.subServices,
          )
        ) {
          return;
        }

        service.subServices.forEach(
          (subService) => {
            if (!subService) {
              return;
            }

            const possibleSubNames = [
              subService.name,
              subService.title,
              subService.id,
              ...(Array.isArray(
                subService.keywords,
              )
                ? subService.keywords
                : []),
            ].filter(Boolean);

            const subMatched =
              possibleSubNames.some(
                (name) => {
                  const normalizedName =
                    normalizeText(
                      name,
                    );

                  if (
                    !normalizedName
                  ) {
                    return false;
                  }

                  return normalizedMessage.includes(
                    normalizedName,
                  );
                },
              );

            if (
              subMatched
            ) {
              addServiceInterest(
                service,
                subService,
              );
            }
          },
        );
      },
    );
  }

  // ==========================================================
  // FORMAT ALL INTERESTED SERVICES FOR EMAILJS
  // ==========================================================

  function formatInterestedServices() {
    const interests =
      interestedServicesRef.current;

    if (
      !interests ||
      interests.length === 0
    ) {
      return "Not selected";
    }

    return interests
      .map(
        (item) => {
          const serviceName =
            item.service ||
            "Unknown Service";

          const subServices =
            item.subServices || [];

          if (
            subServices.length ===
            0
          ) {
            return `• ${serviceName}`;
          }

          return [
            `• ${serviceName}`,

            ...subServices.map(
              (subService) =>
                `  - ${subService}`,
            ),
          ].join("\n");
        },
      )
      .join("\n\n");
  }

  // ==========================================================
  // SHOW SERVICE SUB-SERVICES
  // ==========================================================

  function showSubServices(
    service,
  ) {
    if (
      !service ||
      service.comingSoon ||
      !service.subServices ||
      service.subServices.length === 0
    ) {
      return;
    }

    botSay(
      `Here are the services available under ${service.name}:`,
      {
        type: "subServices",
        service,
      },
    );
  }

  // ==========================================================
  // INITIAL CHAT FLOW
  // ==========================================================

  useEffect(() => {
    if (!isOpen) {
      resetChat();

      return;
    }

    if (greetedRef.current) {
      return;
    }

    greetedRef.current = true;

    // --------------------------------------------------------
    // 1. GREETING
    // --------------------------------------------------------

    const greetingTimer =
      setTimeout(() => {
        botSay(
          "Hi there! 👋 How can I help you?",
        );
      }, 350);

    timersRef.current.push(
      greetingTimer,
    );

    // --------------------------------------------------------
    // 2. AUTOMATIC VISITOR MESSAGE
    // --------------------------------------------------------

    const questionTimer =
      setTimeout(() => {
        if (
          automaticQuestionShownRef.current
        ) {
          return;
        }

        automaticQuestionShownRef.current =
          true;

        userSay(
          "Can you show me what type of services you provide?",
        );
      }, 1500);

    timersRef.current.push(
      questionTimer,
    );

    // --------------------------------------------------------
    // 3. SERVICES
    // --------------------------------------------------------

    const servicesTimer =
      setTimeout(() => {
        botSay(
          "Certainly! Here are the services currently offered:",
          {
            type: "services",
          },
        );
      }, 2050);

    timersRef.current.push(
      servicesTimer,
    );

    // --------------------------------------------------------
    // 4. EMAIL REQUEST
    // --------------------------------------------------------

    const emailTimer =
      setTimeout(() => {
        botSay(
          "Before we continue, please enter your email address.",
        );
      }, 3300);

    timersRef.current.push(
      emailTimer,
    );

    return () => {
      clearAllTimers();
    };
  }, [isOpen]);

  // ==========================================================
  // SERVICE SELECTION
  // ==========================================================

  function handleServiceSelect(
    service,
  ) {
    // --------------------------------------------------------
    // EMAIL REQUIRED
    // --------------------------------------------------------

    if (!emailVerified) {
      withTyping(() => {
        botSay(
          "Before we continue, please enter your email address.",
        );
      });

      return;
    }

    // --------------------------------------------------------
    // NAME REQUIRED
    // --------------------------------------------------------

    if (!nameCollected) {
      withTyping(() => {
        botSay(
          "Please enter your full name before we continue.",
        );
      });

      return;
    }

    // --------------------------------------------------------
    // SAVE CURRENT SERVICE
    // --------------------------------------------------------

    setSelectedService(
      service,
    );

    setSelectedSubService(
      null,
    );

    // --------------------------------------------------------
    // IMPORTANT:
    // Add service to ALL interests.
    // --------------------------------------------------------

    addServiceInterest(
      service,
    );

    // --------------------------------------------------------
    // USER MESSAGE
    // --------------------------------------------------------

    userSay(
      service.name,
    );

    // --------------------------------------------------------
    // PRODUCT DEVELOPMENT
    // --------------------------------------------------------

    if (
      service.comingSoon
    ) {
      withTyping(() => {
        botSay(
          "🚀 Product Development is currently coming soon. Our team is working on this service and it will be available in the future.",
        );
      });

      return;
    }

    // --------------------------------------------------------
    // ACTIVE SERVICE
    // --------------------------------------------------------

    withTyping(() => {
      botSay(
        service.description,
      );

      // ------------------------------------------------------
      // SHOW SUB-SERVICES
      // ------------------------------------------------------

      if (
        service.subServices &&
        service.subServices.length > 0
      ) {
        showSubServices(
          service,
        );
      }
    });
  }

  // ==========================================================
  // SUBSERVICE SELECTION
  // ==========================================================

  function handleSubServiceSelect(
    service,
    subService,
  ) {
    // --------------------------------------------------------
    // EMAIL REQUIRED
    // --------------------------------------------------------

    if (!emailVerified) {
      withTyping(() => {
        botSay(
          "Before we continue, please enter your email address.",
        );
      });

      return;
    }

    // --------------------------------------------------------
    // NAME REQUIRED
    // --------------------------------------------------------

    if (!nameCollected) {
      withTyping(() => {
        botSay(
          "Please enter your full name before we continue.",
        );
      });

      return;
    }

    // --------------------------------------------------------
    // SAVE CURRENT SERVICE
    // --------------------------------------------------------

    setSelectedService(
      service,
    );

    setSelectedSubService(
      subService,
    );

    // --------------------------------------------------------
    // IMPORTANT:
    // Save BOTH service and sub-service to interest list.
    // --------------------------------------------------------

    addServiceInterest(
      service,
      subService,
    );

    // --------------------------------------------------------
    // USER MESSAGE
    // --------------------------------------------------------

    userSay(
      subService.name,
    );

    // --------------------------------------------------------
    // BOT RESPONSE
    // --------------------------------------------------------

    withTyping(() => {
      botSay(
        subService.description,
      );

      // ------------------------------------------------------
      // OFFER OTHER SUB-SERVICES
      // ------------------------------------------------------

      if (
        service.subServices &&
        service.subServices.length > 0
      ) {
        botSay(
          `You can also explore other ${service.name} services below:`,
          {
            type: "subServices",
            service,
            excludeSubService:
              subService.id,
          },
        );
      }
    });
  }

  // ==========================================================
  // SHOW SERVICES AGAIN
  // ==========================================================

  function showServicesAgain() {
    withTyping(() => {
      botSay(
        "Of course. Here are the services we provide:",
        {
          type: "services",
        },
      );
    });
  }

  // ==========================================================
  // ANSWER SERVICE QUESTION
  // ==========================================================

  function answerServiceQuestion(
    message,
  ) {
    // --------------------------------------------------------
    // FIRST: CHECK SUBSERVICE
    // --------------------------------------------------------

    const subServiceResult =
      findSubServiceFromMessage(
        message,
      );

    if (
      subServiceResult
    ) {
      const {
        service,
        subService,
      } =
        subServiceResult;

      // ------------------------------------------------------
      // SAVE CURRENT SELECTION
      // ------------------------------------------------------

      setSelectedService(
        service,
      );

      setSelectedSubService(
        subService,
      );

      // ------------------------------------------------------
      // IMPORTANT:
      // Add this service/sub-service to ALL interests.
      // ------------------------------------------------------

      addServiceInterest(
        service,
        subService,
      );

      botSay(
        `${subService.name}: ${subService.description}`,
      );

      // ------------------------------------------------------
      // SHOW OTHER SUB-SERVICES
      // ------------------------------------------------------

      if (
        service.subServices &&
        service.subServices.length > 0
      ) {
        botSay(
          `You can also explore other ${service.name} services:`,
          {
            type: "subServices",
            service,
            excludeSubService:
              subService.id,
          },
        );
      }

      return true;
    }

    // --------------------------------------------------------
    // SECOND: CHECK MAIN SERVICE
    // --------------------------------------------------------

    const service =
      findServiceFromMessage(
        message,
      );

    if (service) {
      // ------------------------------------------------------
      // SAVE CURRENT SERVICE
      // ------------------------------------------------------

      setSelectedService(
        service,
      );

      setSelectedSubService(
        null,
      );

      // ------------------------------------------------------
      // IMPORTANT:
      // Save service to ALL interests.
      // ------------------------------------------------------

      addServiceInterest(
        service,
      );

      // ------------------------------------------------------
      // PRODUCT DEVELOPMENT
      // ------------------------------------------------------

      if (
        service.comingSoon
      ) {
        botSay(
          `🚀 ${service.name} is coming soon. Our Product Development service is currently under development and will be available soon.`,
        );

        return true;
      }

      // ------------------------------------------------------
      // MAIN SERVICE DESCRIPTION
      // ------------------------------------------------------

      botSay(
        service.description,
      );

      // ------------------------------------------------------
      // SHOW SUB-SERVICES
      // ------------------------------------------------------

      if (
        service.subServices &&
        service.subServices.length > 0
      ) {
        botSay(
          `Here are the services available under ${service.name}:`,
          {
            type: "subServices",
            service,
          },
        );
      }

      return true;
    }

    return false;
  }

  // ==========================================================
  // NEUTRAL RESPONSE
  // ==========================================================

  function showNeutralServiceResponse() {
    botSay(
      "I'd be happy to help with our services. What type of service are you interested in?",
    );

    botSay(
      "You can choose from the services below:",
      {
        type: "services",
      },
    );
  }

  // ==========================================================
  // FINISH CONVERSATION
  // ==========================================================

  async function finishConversation() {
    if (
      !emailVerified ||
      !nameCollected ||
      emailSent
    ) {
      return;
    }

    setIsTyping(true);

    // --------------------------------------------------------
    // IMPORTANT:
    // Get ALL services from the ref.
    // --------------------------------------------------------

    const servicesText =
      formatInterestedServices();

    // --------------------------------------------------------
    // Keep the latest selected service for compatibility.
    // --------------------------------------------------------

    const latestService =
      selectedService?.name ||
      null;

    const latestSubService =
      selectedSubService?.name ||
      null;

    console.log(
      "Final chatbot interests:",
      interestedServicesRef.current,
    );

    console.log(
      "Formatted services:",
      servicesText,
    );

    const sent =
      await sendUserInterestEmail({
        name: userName,

        email: userEmail,

        // ----------------------------------------------------
        // THIS IS WHAT EMAILJS {{services}} RECEIVES
        // ----------------------------------------------------

        services:
          servicesText,

        // ----------------------------------------------------
        // Compatibility with older templates
        // ----------------------------------------------------

        service:
          latestService,

        subService:
          latestSubService,
      });

    setIsTyping(false);

    if (sent) {
      setEmailSent(true);

      botSay(
        "Thank you for chatting with us! 😊 Your details have been shared with our team.",
      );
    } else {
      botSay(
        "Thank you for your time. We appreciate your interest in our services.",
      );
    }
  }

  // ==========================================================
  // PROCESS USER MESSAGE
  // ==========================================================

  async function processUserMessage(
    value,
  ) {
    const message =
      value.trim();

    if (!message) {
      return;
    }

    // --------------------------------------------------------
    // SHOW USER MESSAGE
    // --------------------------------------------------------

    userSay(message);

    setInputValue("");

    const lowerMessage =
      message.toLowerCase();

    // ========================================================
    // EMAIL COLLECTION
    // ========================================================

    if (!emailVerified) {
      if (
        isValidEmail(message)
      ) {
        const normalizedEmail =
          message
            .trim()
            .toLowerCase();

        setUserEmail(
          normalizedEmail,
        );

        setEmailVerified(
          true,
        );

        withTyping(() => {
          botSay(
            "Thanks! Now please enter your full name.",
          );
        });

        return;
      }

      withTyping(() => {
        botSay(
          "Please enter a valid email address.",
        );
      });

      return;
    }

    // ========================================================
    // FULL NAME COLLECTION
    // ========================================================

    if (!nameCollected) {
      const nameLooksValid =
        message.length >= 2 &&
        /[a-zA-Z]/.test(
          message,
        ) &&
        !message.includes("@");

      if (!nameLooksValid) {
        withTyping(() => {
          botSay(
            "Please enter your full name so we can continue.",
          );
        });

        return;
      }

      setUserName(
        message.trim(),
      );

      setNameCollected(
        true,
      );

      withTyping(() => {
        botSay(
          `Thank you, ${message.trim()}. 😊 What type of service are you interested in?`,
        );

        botSay(
          "Please choose a service below:",
          {
            type: "services",
          },
        );
      });

      return;
    }

    // ========================================================
    // IMPORTANT:
    // Detect ALL services mentioned in the user's message.
    //
    // This runs before the individual service matching.
    //
    // Example:
    // "I need web development and WhatsApp API"
    //
    // Both will be stored.
    // ========================================================

    collectServicesFromMessage(
      message,
    );

    // ========================================================
    // END CONVERSATION
    // ========================================================

    const endingKeywords = [
      "bye",
      "goodbye",
      "thank you",
      "thanks",
      "that's all",
      "thats all",
      "no more",
      "end chat",
      "finish",
      "done",
    ];

    const wantsToEnd =
      endingKeywords.some(
        (keyword) =>
          lowerMessage.includes(
            keyword,
          ),
      );

    if (wantsToEnd) {
      await finishConversation();

      return;
    }

    // ========================================================
    // SHOW ALL SERVICES
    // ========================================================

    const asksForServices =
      [
        "show services",
        "show me services",
        "list services",
        "all services",
        "what services",
        "what are your services",
        "what services do you provide",
        "what do you provide",
        "what do you offer",
        "what can you offer",
        "your services",
      ].some(
        (keyword) =>
          lowerMessage.includes(
            keyword,
          ),
      );

    if (asksForServices) {
      showServicesAgain();

      return;
    }

    // ========================================================
    // GENERAL SERVICE QUESTION
    // ========================================================

    const isGeneralServiceQuestion =
      generalServiceKeywords.some(
        (keyword) =>
          lowerMessage.includes(
            keyword.toLowerCase(),
          ),
      );

    if (
      isGeneralServiceQuestion
    ) {
      const answered =
        answerServiceQuestion(
          message,
        );

      if (!answered) {
        showServicesAgain();
      }

      return;
    }

    // ========================================================
    // SPECIFIC SERVICE QUESTION
    // ========================================================

    const answered =
      answerServiceQuestion(
        message,
      );

    if (answered) {
      return;
    }

    // ========================================================
    // UNRELATED QUESTION
    // ========================================================

    withTyping(() => {
      showNeutralServiceResponse();
    });
  }

  // ==========================================================
  // INPUT HANDLER
  // ==========================================================

  function handleSend() {
    if (
      !inputValue.trim() ||
      isTyping
    ) {
      return;
    }

    processUserMessage(
      inputValue,
    );
  }

  // ==========================================================
  // CLOSE HANDLER
  // ==========================================================

  async function handleClose() {
    // --------------------------------------------------------
    // If visitor has completed email + name and hasn't already
    // received an email, send the complete interest list.
    // --------------------------------------------------------

    if (
      emailVerified &&
      nameCollected &&
      !emailSent
    ) {
      await finishConversation();
    }

    clearAllTimers();

    onClose();
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="
        fixed
        bottom-24
        right-6
        z-50
        w-[320px]
        sm:w-[380px]
        h-[520px]
        bg-white
        rounded-3xl
        shadow-[0_20px_60px_rgba(15,23,42,0.20)]
        border
        border-slate-200
        overflow-hidden
        flex
        flex-col
      "
    >
      {/* ====================================================
          HEADER
      ===================================================== */}

      <div
        className="
          relative
          bg-gradient-to-r
          from-orange-600
          via-orange-500
          to-yellow-600
          px-5
          py-4
          flex
          items-center
          gap-3
        "
      >
        <div
          className="
            absolute
            inset-0
            bg-gradient-to-r
            from-white/5
            via-white/10
            to-transparent
            pointer-events-none
          "
        />

        {/* BOT ICON */}

        <div
          className="
            relative
            z-10
            w-10
            h-10
            rounded-full
            bg-white/20
            border
            border-white/20
            backdrop-blur-sm
            flex
            items-center
            justify-center
            text-white
            shadow-sm
          "
        >
          <Bot
            size={22}
            strokeWidth={1.8}
          />
        </div>

        {/* HEADER TEXT */}

        <div
          className="
            relative
            z-10
            flex-1
            min-w-0
          "
        >
          <p
            className="
              text-white
              font-semibold
              text-sm
              truncate
            "
          >
            Web_Bocket Assistant
          </p>

          <div
            className="
              flex
              items-center
              gap-1.5
              mt-0.5
            "
          >
            <span
              className="
                w-1.5
                h-1.5
                rounded-full
                bg-emerald-300
                shadow-[0_0_6px_rgba(110,231,183,0.8)]
              "
            />

            <p
              className="
                text-orange-100
                text-[11px]
              "
            >
              Your Service Assistant
            </p>
          </div>
        </div>

        {/* CLOSE */}

        <button
          onClick={handleClose}
          aria-label="Close chatbot"
          className="
            relative
            z-10
            w-8
            h-8
            rounded-full
            flex
            items-center
            justify-center
            text-white
            hover:bg-white/15
            transition-all
            duration-200
          "
        >
          <X
            size={19}
            strokeWidth={2}
          />
        </button>
      </div>

      {/* ====================================================
          MESSAGES
      ===================================================== */}

      <div
        className="
          flex-1
          overflow-y-auto
          px-4
          py-4
          space-y-3
          no-scrollbar
          bg-gradient-to-b
          from-white
          to-slate-50/60
        "
      >
        {messages.map(
          (message) => (
            <MessageBubble
              key={message.id}
              message={message}
              onServiceSelect={
                handleServiceSelect
              }
              onSubServiceSelect={
                handleSubServiceSelect
              }
              emailVerified={
                emailVerified
              }
              nameCollected={
                nameCollected
              }
            />
          ),
        )}

        {/* ==================================================
            TYPING INDICATOR
        =================================================== */}

        {isTyping && (
          <div className="flex justify-start">
            <div
              className="
                bg-white
                border
                border-slate-200
                rounded-2xl
                rounded-bl-sm
                px-4
                py-3
                flex
                items-center
                gap-1
                shadow-sm
              "
            >
              <span
                className="
                  w-1.5
                  h-1.5
                  bg-slate-400
                  rounded-full
                  animate-bounce
                "
              />

              <span
                className="
                  w-1.5
                  h-1.5
                  bg-slate-400
                  rounded-full
                  animate-bounce
                  [animation-delay:150ms]
                "
              />

              <span
                className="
                  w-1.5
                  h-1.5
                  bg-slate-400
                  rounded-full
                  animate-bounce
                  [animation-delay:300ms]
                "
              />
            </div>
          </div>
        )}

        <div
          ref={messagesEndRef}
        />
      </div>

      {/* ====================================================
          INPUT
      ===================================================== */}

      <div
        className="
          px-4
          py-3
          border-t
          border-slate-100
          bg-white
          flex
          gap-2
        "
      >
        <input
          type={
            !emailVerified
              ? "email"
              : "text"
          }
          value={inputValue}
          onChange={(event) =>
            setInputValue(
              event.target.value,
            )
          }
          onKeyDown={(event) => {
            if (
              event.key ===
              "Enter"
            ) {
              handleSend();
            }
          }}
          placeholder={
            !emailVerified
              ? "Enter your email..."
              : !nameCollected
                ? "Enter your full name..."
                : "Ask about our services..."
          }
          className="
            flex-1
            min-w-0
            border
            border-slate-200
            rounded-full
            px-4
            py-2.5
            text-sm
            text-slate-700
            placeholder:text-slate-400
            outline-none
            bg-slate-50
            focus:bg-white
            focus:border-orange-400
            focus:ring-2
            focus:ring-orange-500/10
            transition-all
          "
        />

        <button
          onClick={handleSend}
          disabled={
            !inputValue.trim() ||
            isTyping
          }
          aria-label="Send message"
          className="
            w-10
            h-10
            shrink-0
            rounded-full
            bg-gradient-to-br
            from-orange-500
            to-yellow-600
            hover:from-orange-600
            hover:to-yellow-700
            disabled:from-orange-300
            disabled:to-yellow-400
            flex
            items-center
            justify-center
            shadow-sm
            hover:shadow-md
            transition-all
            duration-200
            disabled:cursor-not-allowed
          "
        >
          <svg
            className="
              w-4
              h-4
              text-white
            "
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ============================================================
// MESSAGE BUBBLE
// ============================================================

function MessageBubble({
  message,
  onServiceSelect,
  onSubServiceSelect,
  emailVerified,
  nameCollected,
}) {
  const isUser =
    message.role === "user";

  // ==========================================================
  // USER MESSAGE
  // ==========================================================

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div
          className="
            bg-gradient-to-br
            from-blue-600
            to-blue-700
            text-white
            rounded-2xl
            rounded-br-sm
            px-4
            py-2.5
            text-sm
            max-w-[85%]
            shadow-sm
            leading-relaxed
          "
        >
          {message.text}
        </div>
      </div>
    );
  }

  // ==========================================================
  // BOT MESSAGE
  // ==========================================================

  return (
    <div
      className="
        flex
        justify-start
        gap-2
      "
    >
      {/* BOT ICON */}

      <div
        className="
          w-7
          h-7
          shrink-0
          rounded-full
          bg-gradient-to-br
          from-orange-500
          to-yellow-600
          flex
          items-center
          justify-center
          text-white
          shadow-sm
          mt-0.5
        "
      >
        <Bot
          size={15}
          strokeWidth={1.8}
        />
      </div>

      {/* BOT CONTENT */}

      <div
        className="
          max-w-[85%]
          min-w-0
        "
      >
        {/* ==================================================
            NORMAL BOT MESSAGE
        =================================================== */}

        {message.type !==
          "services" &&
          message.type !==
            "subServices" && (
            <div
              className="
                bg-white
                text-slate-800
                border
                border-slate-200
                rounded-2xl
                rounded-bl-sm
                px-4
                py-2.5
                text-sm
                shadow-sm
                leading-relaxed
              "
            >
              {message.text}
            </div>
          )}

        {/* ==================================================
            MAIN SERVICES MESSAGE
        =================================================== */}

        {message.type ===
          "services" && (
          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-2xl
              rounded-bl-sm
              px-4
              py-3
              shadow-sm
              w-full
            "
          >
            {/* HEADING */}

            <p
              className="
                text-sm
                text-slate-800
                leading-relaxed
                mb-3
              "
            >
              {message.text}
            </p>

            {/* SERVICE LIST */}

            <div
              className="
                divide-y
                divide-slate-100
              "
            >
              {availableServices.map(
                (service) => (
                  <button
                    key={
                      service.id
                    }
                    onClick={() =>
                      onServiceSelect(
                        service,
                      )
                    }
                    disabled={
                      !emailVerified ||
                      !nameCollected
                    }
                    className="
                      group
                      w-full
                      flex
                      items-center
                      justify-between
                      gap-3
                      py-2.5
                      text-left
                      transition-all
                      duration-200
                      hover:bg-slate-50
                      rounded-lg
                      px-2
                      disabled:opacity-60
                      disabled:cursor-not-allowed
                    "
                  >
                    {/* SERVICE NAME */}

                    <span
                      className="
                        min-w-0
                        flex-1
                        text-sm
                        font-medium
                        text-slate-700
                        group-hover:text-orange-600
                        transition-colors
                        duration-200
                      "
                    >
                      <span>
                        {
                          service.name
                        }
                      </span>

                      {/* COMING SOON */}

                      {service.comingSoon && (
                        <span
                          className="
                            inline-flex
                            ml-2
                            px-1.5
                            py-0.5
                            rounded-full
                            text-[9px]
                            font-semibold
                            uppercase
                            tracking-wide
                            bg-orange-50
                            text-orange-500
                            border
                            border-orange-100
                            align-middle
                          "
                        >
                          Coming Soon
                        </span>
                      )}
                    </span>

                    {/* ARROW */}

                    <span
                      className="
                        shrink-0
                        text-slate-400
                        group-hover:text-orange-500
                        group-hover:translate-x-0.5
                        transition-all
                        duration-200
                      "
                    >
                      →
                    </span>
                  </button>
                ),
              )}
            </div>
          </div>
        )}

        {/* ==================================================
            SUB-SERVICES MESSAGE
        =================================================== */}

        {message.type ===
          "subServices" &&
          message.service && (
            <div
              className="
                bg-white
                border
                border-slate-200
                rounded-2xl
                rounded-bl-sm
                px-4
                py-3
                shadow-sm
                w-full
              "
            >
              {/* HEADING */}

              <p
                className="
                  text-sm
                  text-slate-800
                  font-medium
                  leading-relaxed
                  mb-1
                "
              >
                {message.text}
              </p>

              {/* SERVICE NAME */}

              <p
                className="
                  text-[11px]
                  text-slate-400
                  mb-3
                "
              >
                Select an option to learn
                more.
              </p>

              {/* SUB-SERVICE LIST */}

              <div
                className="
                  divide-y
                  divide-slate-100
                "
              >
                {message.service.subServices
                  .filter(
                    (subService) =>
                      subService.id !==
                      message.excludeSubService,
                  )
                  .map(
                    (
                      subService,
                    ) => (
                      <button
                        key={
                          subService.id
                        }
                        onClick={() =>
                          onSubServiceSelect(
                            message.service,
                            subService,
                          )
                        }
                        disabled={
                          !emailVerified ||
                          !nameCollected
                        }
                        className="
                          group
                          w-full
                          flex
                          items-center
                          justify-between
                          gap-3
                          py-2.5
                          px-2
                          text-left
                          rounded-lg
                          transition-all
                          duration-200
                          hover:bg-orange-50/70
                          disabled:opacity-60
                          disabled:cursor-not-allowed
                        "
                      >
                        {/* SUB-SERVICE NAME */}

                        <span
                          className="
                            min-w-0
                            flex-1
                            text-sm
                            font-medium
                            text-slate-700
                            group-hover:text-orange-600
                            transition-colors
                            duration-200
                          "
                        >
                          {
                            subService.name
                          }
                        </span>

                        {/* ARROW */}

                        <span
                          className="
                            shrink-0
                            w-6
                            h-6
                            rounded-full
                            flex
                            items-center
                            justify-center
                            text-slate-400
                            group-hover:text-orange-500
                            group-hover:bg-orange-50
                            group-hover:translate-x-0.5
                            transition-all
                            duration-200
                          "
                        >
                          →
                        </span>
                      </button>
                    ),
                  )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}